/**
 * The check-in's ticket grouping, on the session detail page: one RepairShopr
 * ticket covering every device, or one per device.
 *
 * Two states, and both matter:
 *
 *  - **Editable**, while no device carries a ticket yet — reception picked the
 *    tile on the create page, and a mis-pick has to be recoverable without
 *    cancelling the check-in and losing an OTP-verified mobile and an accepted
 *    terms snapshot with it. `canEditTicketGrouping` mirrors the server's rule.
 *  - **Read-only**, once the tickets exist — it still renders, because a
 *    technician looking at one ticket covering three laptops otherwise has no
 *    way to tell that was a decision rather than a default.
 *
 * It sits with the device list because that is where the consequence shows.
 *
 * On-system styling (sm4rt-web-ui skill): shared RadioOption boxes for the
 * single-select (§12), design-system greys and 11px uppercase label (§1–2).
 */

import React from 'react';
import type { CheckInSession, CheckInTicketGrouping } from '@techgitter/contracts';

import { RadioOption } from '../../panel/RadioOption';
import { canEditTicketGrouping, ticketGroupingLabel } from './sessionDetailHelpers';
import './TicketGroupingControl.css';

export interface TicketGroupingControlProps {
  session: CheckInSession;
  onChange: (grouping: CheckInTicketGrouping) => void;
  /** Disables the inputs while a save is in flight. */
  isSaving?: boolean;
}

const HELP: Record<CheckInTicketGrouping, string> = {
  single_job: 'One ticket, one status, one invoice for everything checked in.',
  job_per_device: 'Each device tracked and billed on its own ticket.',
};

export const TicketGroupingControl: React.FC<TicketGroupingControlProps> = ({
  session,
  onChange,
  isSaving,
}) => {
  const grouping = (session.ticketGrouping ?? 'job_per_device') as CheckInTicketGrouping;
  const editable = canEditTicketGrouping(session);

  if (!editable) {
    return (
      <div className="ci-grouping ci-grouping--readonly">
        <span className="ci-grouping__label">Ticket grouping</span>
        <span className="ci-grouping__value">{ticketGroupingLabel(grouping)}</span>
        <span className="ci-grouping__help">{HELP[grouping]}</span>
      </div>
    );
  }

  return (
    <div className="ci-grouping">
      <span className="ci-grouping__label">Ticket grouping</span>
      <div className="radio-option-group radio-option-group--row">
        <RadioOption
          name="ticketGrouping"
          value="single_job"
          checked={grouping === 'single_job'}
          onChange={() => onChange('single_job')}
          label="One job"
          description={HELP.single_job}
          disabled={isSaving}
        />
        <RadioOption
          name="ticketGrouping"
          value="job_per_device"
          checked={grouping === 'job_per_device'}
          onChange={() => onChange('job_per_device')}
          label="Job per device"
          description={HELP.job_per_device}
          disabled={isSaving}
        />
      </div>
    </div>
  );
};
